import React, { useEffect, useRef } from "react";
import { Button } from "@progress/kendo-react-buttons";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { requiredValidator } from "../../components/formFields/CommonValidator";
import FormTextField from "../../components/formFields/FormTextField";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingPanel } from "../../components/layout/Loading";
import {
  createSightseen,
  getAllActiveSightseen,
  getAllSightseen,
  getSightseenByID,
  sightseenImageUpload,
  updateSightseen,
} from "./services/sightseen.services";
import { clearSightseenDetails } from "./sightseenSlice";
import { Typography } from "@progress/kendo-react-common";
import { RxCross1 } from "react-icons/rx";
import { STATUSARRAY } from "../../_contstants/common";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import FormNumericTextField from "../../components/formFields/FormNumericTextField";
import { getAllActiveSectors } from "../sector/services/sector.services";
import { closed } from "../../components/drawer/drawerSlice";
import IconButton from "../../components/common/IconButton";
import FormTextArea from "../../components/formFields/FormTextArea";
import FormImageUpload from "../../components/formFields/FormImageUpload";

const CreateSightseen: React.FC = () => {
  const dispatch = useAppDispatch();
  const gridRef = useRef<any>(null);

  const SightseenDetail = useAppSelector(
    (state) => state.sightseen.SightseenDetail
  );
  const SightseenID = useAppSelector((state) => state.drawer.data);
  const formLoading = useAppSelector((state) => state.sightseen.formLoading);
  const SectorList = useAppSelector((state) => state.sector.SectorList);
  const isIndirect = useAppSelector((state) => state.drawer.isIndirect);

  useEffect(() => {
    dispatch(getAllActiveSectors());
    return () => {
      dispatch(clearSightseenDetails());
      dispatch(closed());
    };
  }, []);

  useEffect(() => {
    if (SightseenID) {
      dispatch(getSightseenByID(SightseenID));
    }
  }, [SightseenID]);

  const handleSubmit = async (values: any) => {
    const ImageData = new FormData();
    const file =
      values?.SightSeenImage && typeof values?.SightSeenImage !== "string"
        ? values.SightSeenImage[0].getRawFile()
        : "";

    const formData = {
      SectorId: values?.SectorId,
      SightseenName: values?.SightseenName,
      Description: values?.Description,
      Rate: values?.Rate ? +values?.Rate : null,
      IsActive: values?.IsActive === 1,
    };
    try {
      if (!SightseenID) {
        const response = await dispatch(createSightseen(formData));
        if (response?.meta?.requestStatus === "fulfilled") {
          if (
            values?.SightSeenImage &&
            typeof values?.SightSeenImage !== "string" &&
            response?.payload?.Data?.ID
          ) {
            ImageData.append("SightSeenImage", file);
            ImageData.append("ID", response?.payload?.Data?.ID);

            await dispatch(sightseenImageUpload(ImageData));
          }
          dispatch(closed());
          dispatch(clearSightseenDetails());
          if (isIndirect) {
            dispatch(getAllActiveSightseen());
          } else {
            dispatch(getAllSightseen());
          }
        }
      } else {
        const editPayload = {
          ...formData,
          ID: SightseenID,
        };
        const response = await dispatch(updateSightseen(editPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          if (
            values.SightSeenImage &&
            typeof values.SightSeenImage !== "string"
          ) {
            ImageData.append("SightSeenImage", file);
            ImageData.append("ID", values?.ID ? values?.ID : "");

            await dispatch(sightseenImageUpload(ImageData));
          }
          dispatch(closed());
          dispatch(clearSightseenDetails());
          dispatch(getAllSightseen());
        }
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      throw error;
    }
  };

  const handleFormClose = () => {
    dispatch(closed());
  };

  if (formLoading) return <LoadingPanel gridRef={gridRef} />;

  return (
    <Form
      onSubmit={handleSubmit}
      initialValues={SightseenDetail}
      render={(formRenderProps: FormRenderProps) => (
        <FormElement style={{ width: "100%" }}>
          <GridLayout
            style={{
              display: "flex",
              flexWrap: "wrap",
            }}
            gap={{ rows: 10 }}
            cols={[{ width: "100%" }]}
          >
            <GridLayoutItem
              style={{
                width: "100%",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                margin: "17px 0 8px",
              }}
            >
              <Typography.h4 className="m-0">
                {SightseenID ? "Update Sightseen " : "Add Sightseen"}
              </Typography.h4>
              <IconButton
                type="button"
                fillMode={"flat"}
                themeColor={"dark"}
                style={{ marginRight: 4 }}
                onClick={() => dispatch(closed())}
                icon={RxCross1}
              />
            </GridLayoutItem>
            <GridLayoutItem style={{ width: "100%" }}>
              <Field
                name="SightseenName"
                label="Sightseen"
                placeholder="Sightseen"
                component={FormTextField}
                validator={requiredValidator}
                astrike={true}
              />
            </GridLayoutItem>
            <GridLayoutItem style={{ width: "100%" }}>
              <Field
                id={"SectorId"}
                name={"SectorId"}
                placeholder={"Sector"}
                component={FormSelectionField}
                label={"Sector"}
                validator={requiredValidator}
                astrike={true}
                options={SectorList?.map((sector: any) => {
                  return {
                    value: sector.ID,
                    label: sector.SectorName,
                  };
                })}
              />
            </GridLayoutItem>
            <GridLayoutItem style={{ width: "100%" }}>
              <Field
                wrapperClassName="right-aligned-field"
                name="Rate"
                label="Rate"
                placeholder="0"
                component={FormNumericTextField}
                validator={requiredValidator}
                astrike={true}
              />
            </GridLayoutItem>
            <GridLayoutItem style={{ width: "100%" }}>
              <Field
                name="Description"
                label="Description"
                placeholder="Description"
                component={FormTextArea}
              />
            </GridLayoutItem>
            <GridLayoutItem
              style={{
                width: "100%",
              }}
            >
              <Field
                name="SightSeenImage"
                type="file"
                accept="image/*"
                label="Sightseen Image"
                component={FormImageUpload}
              />
            </GridLayoutItem>
            <GridLayoutItem style={{ width: "100%" }}>
              <Field
                id={"IsActive"}
                name={"IsActive"}
                placeholder="i.e. Active"
                component={FormSelectionField}
                validator={requiredValidator}
                astrike={true}
                label={"Status"}
                options={STATUSARRAY?.map((status: any) => {
                  return {
                    value: status.value,
                    label: status.label,
                  };
                })}
              />
            </GridLayoutItem>
            <GridLayoutItem
              colSpan={3}
              style={{
                textAlign: "start",
                marginTop: "20px",
                width: "100%",
              }}
            >
              <ButtonWithLoading
                label={SightseenID ? "Update" : "Save"}
                type="submit"
                disabled={!formRenderProps.allowSubmit || formLoading}
                loading={formLoading}
              />
              <Button
                type="button"
                fillMode={"outline"}
                themeColor={"primary"}
                style={{ marginLeft: 10 }}
                onClick={handleFormClose}
              >
                Cancel
              </Button>
            </GridLayoutItem>
          </GridLayout>
        </FormElement>
      )}
    />
  );
};

export default CreateSightseen;
